import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import SortableTable from '../components/SortableTable';
import { Warning, ArrowsClockwise, TrendDown, TrendUp, ChartBar } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PerformanceAlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/performance-alerts`, { withCredentials: true });
      setAlerts(res.data);
    } catch { toast.error('Failed to load alerts'); }
    setLoading(false);
  };

  useEffect(() => { fetchAlerts(); }, []);

  const recalculate = async () => {
    setRecalculating(true);
    try {
      await axios.post(`${API_URL}/api/admin/recalculate-scores`, {}, { withCredentials: true });
      toast.success('Scores recalculated');
      fetchAlerts();
    } catch { toast.error('Failed'); }
    setRecalculating(false);
  };

  const critical = alerts.filter(a => a.health === 'critical');
  const warnings = alerts.filter(a => a.health === 'warning');

  const columns = [
    { key: 'name', label: 'Seller', sortable: true, render: (v, row) => (
      <div>
        <p className="text-sm font-semibold text-[#0A0A0A]">{v}</p>
        <p className="text-[11px] text-[#9c9db7]">{row.email}{row.business_name ? ` · ${row.business_name}` : ''}</p>
      </div>
    )},
    { key: 'score', label: 'Score', sortable: true, render: (v, row) => {
      const color = row.health === 'critical' ? '#ea4b5a' : '#f6a609';
      return (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: color }}>{v}</div>
          <span className={`text-xs font-bold uppercase ${row.health === 'critical' ? 'text-[#ea4b5a]' : 'text-[#f6a609]'}`}>{row.health}</span>
        </div>
      );
    }},
    { key: 'fulfillment_rate', label: 'Fulfillment', sortable: true, render: (v) => (
      <div className="flex items-center gap-1">
        {v >= 80 ? <TrendUp size={12} className="text-[#28cd8c]" /> : <TrendDown size={12} className="text-[#ea4b5a]" />}
        <span className="text-sm font-semibold">{v}%</span>
      </div>
    )},
    { key: 'cancellation_rate', label: 'Cancellation', sortable: true, render: (v) => (
      <span className={`text-sm font-semibold ${v > 10 ? 'text-[#ea4b5a]' : 'text-[#0A0A0A]'}`}>{v}%</span>
    )},
    { key: 'return_rate', label: 'Returns', sortable: true, render: (v) => (
      <span className={`text-sm font-semibold ${v > 5 ? 'text-[#f6a609]' : 'text-[#0A0A0A]'}`}>{v}%</span>
    )},
    { key: 'last_calculated', label: 'Last Updated', render: (v) => <span className="text-xs text-[#5a5c87]">{v ? new Date(v).toLocaleString() : 'Never'}</span> },
  ];

  if (loading) return <DashboardLayout title="Performance Alerts"><div className="p-12 text-center"><div className="spinner mx-auto"></div></div></DashboardLayout>;

  return (
    <DashboardLayout title="Performance Alerts" subtitle="Sellers needing attention" actions={
      <button onClick={recalculate} disabled={recalculating}
        className="flex items-center gap-2 px-4 py-2 bg-[#7d64ff] text-white text-sm font-semibold rounded-lg hover:bg-[#6153a7] disabled:opacity-50"
        data-testid="recalculate-btn">
        <ArrowsClockwise size={16} className={recalculating ? 'animate-spin' : ''} />
        {recalculating ? 'Recalculating...' : 'Recalculate All'}
      </button>
    }>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="alert-summary">
        <div className="bg-white border border-[#e5e5ea] rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#ea4b5a]/10 flex items-center justify-center"><Warning size={20} className="text-[#ea4b5a]" weight="fill" /></div>
            <div>
              <p className="text-2xl font-bold text-[#ea4b5a]">{critical.length}</p>
              <p className="text-xs text-[#5a5c87] font-medium">Critical Sellers</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-[#e5e5ea] rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#f6a609]/10 flex items-center justify-center"><Warning size={20} className="text-[#f6a609]" weight="fill" /></div>
            <div>
              <p className="text-2xl font-bold text-[#f6a609]">{warnings.length}</p>
              <p className="text-xs text-[#5a5c87] font-medium">Warning Sellers</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-[#e5e5ea] rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#7d64ff]/10 flex items-center justify-center"><ChartBar size={20} className="text-[#7d64ff]" weight="fill" /></div>
            <div>
              <p className="text-2xl font-bold text-[#0A0A0A]">{alerts.length}</p>
              <p className="text-xs text-[#5a5c87] font-medium">Total At-Risk</p>
            </div>
          </div>
        </div>
      </div>

      {alerts.length === 0 ? (
        <div className="bg-white border border-[#e5e5ea] rounded-xl p-12 text-center">
          <div className="w-14 h-14 bg-[#28cd8c]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <TrendUp size={28} className="text-[#28cd8c]" />
          </div>
          <h3 className="text-lg font-bold text-[#0A0A0A] mb-1">All Sellers Healthy</h3>
          <p className="text-sm text-[#5a5c87]">No sellers are below performance thresholds</p>
        </div>
      ) : (
        <SortableTable columns={columns} data={alerts} emptyMessage="No alerts" pageSize={20} />
      )}
    </DashboardLayout>
  );
}
